import React, { useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Link,
  Typography,
  Card,
} from "@mui/material";
import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Confetti from "react-confetti";
import { HugeiconsIcon } from "@hugeicons/react";
import { CheckmarkCircle02Icon } from "@hugeicons-pro/core-solid-standard";
import {
  AlertCircleIcon,
  UserCircleIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { Center, Inline, Stack } from "components/layout/reusableLayouts";
import { ACTIVATE_PHYSICAL_CARD } from "graphql/card";
import { useBrandingContext } from "context/BrandingContext";
import CompanyPaymentCard from "components/common/cards/CompanyPaymentCard";

const DIGIT_COUNT = 4;

interface Last4InputProps {
  value: string;
  onChange: (next: string) => void;
  error?: boolean;
  disabled?: boolean;
}

const Last4Input: React.FC<Last4InputProps> = ({
  value,
  onChange,
  error,
  disabled,
}) => {
  const { t } = useTranslation();
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const focusBox = (index: number) => {
    inputsRef.current[index]?.focus();
    inputsRef.current[index]?.select();
  };

  const handleChange =
    (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value.replace(/\D/g, "");
      const chars = value.split("");
      if (!raw) {
        chars[index] = "";
        onChange(chars.join("").slice(0, DIGIT_COUNT));
        return;
      }
      chars[index] = raw.slice(-1);
      onChange(chars.join("").slice(0, DIGIT_COUNT));
      if (index < DIGIT_COUNT - 1) focusBox(index + 1);
    };

  const handleKeyDown =
    (index: number) => (event: React.KeyboardEvent<HTMLInputElement>) => {
      const chars = value.split("");
      if (event.key === "Backspace") {
        event.preventDefault();
        if (chars[index]) {
          chars[index] = "";
          onChange(chars.join(""));
        } else if (index > 0) {
          chars[index - 1] = "";
          onChange(chars.join(""));
          focusBox(index - 1);
        }
      } else if (event.key === "ArrowLeft" && index > 0) {
        focusBox(index - 1);
      } else if (event.key === "ArrowRight" && index < DIGIT_COUNT - 1) {
        focusBox(index + 1);
      }
    };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, DIGIT_COUNT);
    if (!pasted) return;
    event.preventDefault();
    onChange(pasted);
    focusBox(Math.min(pasted.length, DIGIT_COUNT - 1));
  };

  return (
    <Inline gap="var(--space-3)" justify="center" sx={{ width: "100%" }}>
      {Array.from({ length: DIGIT_COUNT }).map((_, index) => (
        <Box
          key={index}
          component="input"
          ref={(el: HTMLInputElement | null) => {
            inputsRef.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          aria-label={
            t("Digit {{position}}", { position: index + 1 }) as string
          }
          maxLength={1}
          disabled={disabled}
          value={value[index] ?? ""}
          onChange={handleChange(index)}
          onKeyDown={handleKeyDown(index)}
          onPaste={handlePaste}
          onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
          sx={{
            width: "56px",
            height: "56px",
            flexShrink: 0,
            textAlign: "center",
            typography: "h3",
            color: "var(--color-text-primary)",
            backgroundColor: "var(--color-bg-surface)",
            border: `var(--border-width-1) solid ${
              error ? "var(--color-border-danger)" : "var(--color-border)"
            }`,
            borderRadius: "var(--radius-lg)",
            outline: "none",
            caretColor: "var(--color-text-brand)",
            transition: "border-color 120ms ease, box-shadow 120ms ease",
            "&:focus": {
              borderColor: error
                ? "var(--color-border-danger)"
                : "var(--color-border-focus)",
              boxShadow: `0 0 0 var(--border-width-2) ${
                error
                  ? "var(--color-border-danger)"
                  : "var(--color-border-focus)"
              }`,
            },
            "&:disabled": {
              cursor: "not-allowed",
              color: "var(--color-text-disabled)",
            },
          }}
        />
      ))}
    </Inline>
  );
};

interface IdentityNoticeProps {
  mismatch: boolean;
  title: string;
  description: string;
}

const IdentityNotice: React.FC<IdentityNoticeProps> = ({
  mismatch,
  title,
  description,
}) => (
  // Polite on load; the mismatch remounts so its alert is announced as new.
  <Alert
    key={mismatch ? "mismatch" : "identity"}
    role={mismatch ? "alert" : "status"}
    severity={mismatch ? "error" : "info"}
    icon={
      <HugeiconsIcon
        icon={mismatch ? AlertCircleIcon : UserCircleIcon}
        size={20}
      />
    }
    sx={{ width: "100%" }}
  >
    <AlertTitle>{title}</AlertTitle>
    {description}
  </Alert>
);

const OWNERSHIP_ERRORS = [
  "No physical card found with the last 4 digits provided",
  "No physical cards to activate for this user",
];

const isOwnershipError = (message: string) =>
  OWNERSHIP_ERRORS.some((hint) => message.includes(hint));

export interface ActivateCardFormProps {
  signedInName?: string;
  onSignOut?: () => void;
  onActivated?: () => void;
}

// Callers own the surrounding shell and must supply the DS theme.
const ActivateCardForm: React.FC<ActivateCardFormProps> = ({
  signedInName,
  onSignOut,
  onActivated,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { platform } = useBrandingContext();
  const supportEmail = platform?.email || "support@outpave.com";

  const [last4, setLast4] = useState("");
  const [errorText, setErrorText] = useState<string>();
  const [mismatch, setMismatch] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const [activated, setActivated] = useState(false);

  const [activatePhysicalCard, { loading }] = useMutation(
    ACTIVATE_PHYSICAL_CARD,
    {
      onCompleted: (data) => {
        if (data?.activatePhysicalCard) {
          toast.success(t("Physical card is active!"));
          setActivated(true);
          onActivated?.();
          return;
        }
        const message = t("Something went wrong!");
        toast.error(message);
        setErrorText(message);
      },
      onError: (error) => {
        const raw = error?.message || "";
        if (isOwnershipError(raw)) {
          const message = t(
            "No card with these digits is issued to this account",
          );
          toast.error(message);
          setErrorText(message);
          setMismatch(true);
          setHasFailed(true);
          return;
        }
        const message = raw || t("Something went wrong!");
        toast.error(message);
        setErrorText(message);
      },
    },
  );

  const handleLast4Change = (next: string) => {
    setLast4(next);
    if (errorText) setErrorText(undefined);
    if (mismatch) setMismatch(false);
  };

  const handleActivate = async () => {
    try {
      await activatePhysicalCard({ variables: { last4 } });
    } catch {
      // onError handles the user-facing feedback; swallow the rejection.
    }
  };

  const handleReset = () => {
    setActivated(false);
    setLast4("");
    setErrorText(undefined);
    setMismatch(false);
    setHasFailed(false);
  };

  // Conditional by default, direct once the mismatch is confirmed.
  const signOutLink = onSignOut ? (
    <Typography
      variant="body2"
      sx={{ color: "var(--color-text-secondary)", textAlign: "center" }}
    >
      {!hasFailed && signedInName
        ? `${t("Not {{name}}?", { name: signedInName })} `
        : null}
      <Link component="button" type="button" onClick={onSignOut}>
        {hasFailed || !signedInName
          ? t("Sign in with a different account")
          : t("Sign out")}
      </Link>
    </Typography>
  ) : null;

  const supportLine = (
    <Typography
      variant="body2"
      sx={{ color: "var(--color-text-secondary)", textAlign: "center" }}
    >
      {t("Need help? Contact us at ")}
      <Link href={`mailto:${supportEmail}`}>{supportEmail}</Link>
    </Typography>
  );

  const renderBody = () => {
    if (activated) {
      return (
        <>
          <Confetti
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              pointerEvents: "none",
              zIndex: 1,
              borderRadius: "var(--radius-lg)",
              margin: "0 auto",
            }}
          />
          <Card
            variant="outlined"
            sx={{
              display: "flex",
              flexDirection: "column",
              padding: "var(--space-5)",
              minWidth: 0,
            }}
          >
            <Stack
              gap="var(--space-4)"
              sx={{ alignItems: "center", textAlign: "center" }}
            >
              <HugeiconsIcon
                icon={CheckmarkCircle02Icon}
                size={56}
                color="var(--color-text-success)"
              />
              <Typography variant="h3" component="h2">
                {t("Your card is activated")}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "var(--color-text-secondary)" }}
              >
                {t("You are all set to start spending with your new card")}
              </Typography>
              <Stack gap="var(--space-3)" sx={{ width: "100%" }}>
                <Button
                  variant="primary-filled"
                  fullWidth
                  onClick={() => navigate("/my/cards")}
                >
                  {t("View my cards")}
                </Button>
                <Button
                  variant="default-outlined"
                  fullWidth
                  onClick={handleReset}
                >
                  {t("Activate another card")}
                </Button>
              </Stack>
            </Stack>
          </Card>
        </>
      );
    }

    return (
      <Stack gap="var(--space-6)" sx={{ alignItems: "center" }}>
        {signedInName && (
          <IdentityNotice
            mismatch={mismatch}
            title={
              mismatch
                ? (t("This card isn't issued to {{name}}", {
                    name: signedInName,
                  }) as string)
                : (t("Activating as {{name}}", {
                    name: signedInName,
                  }) as string)
            }
            description={
              mismatch
                ? (t(
                    "Sign in with the account the card was issued to, then try again",
                  ) as string)
                : (t(
                    "Only cards issued to this account can be activated",
                  ) as string)
            }
          />
        )}

        {/* No slots: the values are on the plastic. Named on the wrapper,
            because a background-painted face carries no accessible name. */}
        <Box
          role="img"
          aria-label={t("Physical Card") as string}
          sx={{ display: "flex", width: "100%", justifyContent: "center" }}
        >
          <CompanyPaymentCard width="100%" sx={{ maxWidth: 420 }} />
        </Box>

        <Stack
          gap="var(--space-2)"
          sx={{ alignItems: "center", textAlign: "center" }}
        >
          <Typography variant="h5" component="h2">
            {t("Enter the last 4 digits of your new card")}
          </Typography>
        </Stack>

        <Stack
          gap="var(--space-2)"
          sx={{ width: "100%", alignItems: "center", textAlign: "center" }}
        >
          <Last4Input
            value={last4}
            onChange={handleLast4Change}
            error={!!errorText}
            disabled={loading}
          />
          {errorText && (
            <Typography
              variant="caption"
              sx={{ color: "var(--color-text-danger)" }}
            >
              {errorText}
            </Typography>
          )}
        </Stack>

        <Button
          variant="primary-filled"
          fullWidth
          onClick={handleActivate}
          disabled={last4.length !== 4 || loading}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t("Activate Card")
          )}
        </Button>

        {signOutLink}
        {supportLine}
      </Stack>
    );
  };

  return (
    <Center
      maxWidth="420px"
      sx={{ margin: "0 auto", width: "100%", position: "relative" }}
    >
      {renderBody()}
    </Center>
  );
};

export default ActivateCardForm;
